import React from 'react';
import { ArrowDownLeft, ArrowUpRight, BarChart3, Pencil, Percent, Printer, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SearchableSelect from '@/components/shared/SearchableSelect';
import PersianDatePickerFa from '@/components/shared/PersianDatePickerFa';
import { formatRial } from '@/lib/formatMoney';
import type { LookupProject } from '@/lib/api/accounting';
import type { ExpenseEntry } from '@/lib/mock/expenses';

export interface ProjectWorkspaceOverviewTabProps {
  project: LookupProject | null;
  contractAmount: number;
  totalExpense: number;
  totalDeposits: number;
  totalWithdrawals: number;
  ledgerMovements: ExpenseEntry[];
  ledgerDeposits: number;
  ledgerWithdrawals: number;
  beneficiaryFilterOptions: Array<{ value: string; label: string }>;
  filterBeneficiaryId: string;
  setFilterBeneficiaryId: (value: string) => void;
  filterDateFrom: string;
  filterDateTo: string;
  handleFilterDateFromChange: (value: string) => void;
  handleFilterDateToChange: (value: string) => void;
  applyLedgerDateFilter: (dateFa: string) => void;
  filterDateError?: string;
  filterDirection: '' | 'deposit' | 'withdrawal';
  setFilterDirection: (value: '' | 'deposit' | 'withdrawal') => void;
  filterAmountMin: string;
  setFilterAmountMin: (value: string) => void;
  filterAmountMax: string;
  setFilterAmountMax: (value: string) => void;
  filterTypeId: string;
  setFilterTypeId: (value: string) => void;
  filterTypeLabel: string;
  setFilterTypeLabel: (value: string) => void;
  filterDocNo: string;
  setFilterDocNo: (value: string) => void;
  filterDescription: string;
  setFilterDescription: (value: string) => void;
  clearLedgerFilters: () => void;
  advancesError?: string;
  onPrintVoucher?: (entry: ExpenseEntry) => void;
  canEditExpenseType?: (entry: ExpenseEntry) => boolean;
  onEditExpenseType?: (entry: ExpenseEntry) => void;
  grossProfit: number;
  expenseRatio: number;
  progressPercent: number;
  expectedFinalCost: number;
  expectedProfitAtCompletion: number;
  expenseByType: Array<{ label: string; value: number }>;
  expenseByBeneficiary: Array<{ label: string; value: number }>;
  maxTypeValue: number;
  maxBeneficiaryValue: number;
  statusMeta: (status: string) => {
    label: string;
    icon: React.ReactNode;
    className: string;
  };
}

function movementKind(row: ExpenseEntry): 'deposit' | 'withdrawal' {
  return Number(row.credit ?? 0) > 0 ? 'deposit' : 'withdrawal';
}

function typeLabel(row: ExpenseEntry): string {
  if (row.accountTitle) return row.accountTitle;
  if (movementKind(row) === 'deposit') return 'واریز';
  return 'پرداخت';
}

function FilterCellButton({
  title,
  onClick,
  className,
  children,
}: {
  title: string;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`max-w-full truncate text-sky-700 underline-offset-2 hover:text-sky-900 hover:underline ${className || ''}`}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

const ProjectWorkspaceOverviewTab: React.FC<ProjectWorkspaceOverviewTabProps> = ({
  project,
  contractAmount,
  totalExpense,
  totalDeposits,
  totalWithdrawals,
  ledgerMovements,
  ledgerDeposits,
  ledgerWithdrawals,
  beneficiaryFilterOptions,
  filterBeneficiaryId,
  setFilterBeneficiaryId,
  filterDateFrom,
  filterDateTo,
  handleFilterDateFromChange,
  handleFilterDateToChange,
  applyLedgerDateFilter,
  filterDateError,
  filterDirection,
  setFilterDirection,
  filterAmountMin,
  setFilterAmountMin,
  filterAmountMax,
  setFilterAmountMax,
  filterTypeId,
  setFilterTypeId,
  filterTypeLabel,
  setFilterTypeLabel,
  filterDocNo,
  setFilterDocNo,
  filterDescription,
  setFilterDescription,
  clearLedgerFilters,
  advancesError,
  onPrintVoucher,
  canEditExpenseType,
  onEditExpenseType,
  grossProfit,
  expenseRatio,
  progressPercent,
  expectedFinalCost,
  expectedProfitAtCompletion,
  expenseByType,
  expenseByBeneficiary,
  maxTypeValue,
  maxBeneficiaryValue,
  statusMeta,
}) => {
  const hasActiveFilter = Boolean(
    filterBeneficiaryId ||
      filterDateFrom ||
      filterDateTo ||
      filterDirection ||
      filterAmountMin ||
      filterAmountMax ||
      filterTypeId ||
      filterTypeLabel ||
      filterDocNo ||
      filterDescription,
  );

  const applyTypeFilter = (row: ExpenseEntry) => {
    const label = typeLabel(row);
    if (row.expenseTypeId) {
      setFilterTypeId(row.expenseTypeId);
      setFilterTypeLabel('');
    } else if (label && label !== '—') {
      setFilterTypeId('');
      setFilterTypeLabel(label);
    }
  };

  const applyAmountFilter = (amount: number, direction: '' | 'deposit' | 'withdrawal') => {
    const raw = String(Math.round(amount));
    setFilterAmountMin(raw);
    setFilterAmountMax(raw);
    if (direction) setFilterDirection(direction);
  };

  return (
    <div className="flex min-w-0 flex-col gap-10 pt-2">
      <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">وضعیت پروژه</CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const meta = statusMeta(project?.status || '');
              return (
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs ${meta.className}`}>
                  {meta.icon}
                  {meta.label}
                </span>
              );
            })()}
          </CardContent>
        </Card>
        <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">مبلغ پیمان</CardTitle>
          </CardHeader>
          <CardContent className="text-lg font-semibold">{formatRial(contractAmount)}</CardContent>
        </Card>
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="inline-flex items-center gap-1 text-sm text-emerald-700">
              <ArrowDownLeft className="h-4 w-4" />
              جمع واریز
            </CardTitle>
          </CardHeader>
          <CardContent className="text-lg font-semibold text-emerald-800">{formatRial(totalDeposits)}</CardContent>
        </Card>
        <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="inline-flex items-center gap-1 text-sm text-rose-700">
              <ArrowUpRight className="h-4 w-4" />
              جمع برداشت
            </CardTitle>
          </CardHeader>
          <CardContent className="text-lg font-semibold text-rose-800">{formatRial(totalWithdrawals)}</CardContent>
        </Card>
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">جمع هزینه (برداشت)</CardTitle>
          </CardHeader>
          <CardContent className="text-lg font-semibold">{formatRial(totalExpense)}</CardContent>
        </Card>
        <Card
          className={
            grossProfit >= 0
              ? 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white'
              : 'border-rose-200 bg-gradient-to-br from-rose-50 to-white'
          }
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">سود / زیان ناخالص</CardTitle>
          </CardHeader>
          <CardContent className="text-lg font-semibold">{formatRial(grossProfit)}</CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="space-y-3 border-b bg-slate-50/80">
          <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base text-slate-900">
            <span>گردش مالی پروژه</span>
            <span className="text-sm font-semibold tabular-nums text-slate-600">
              {ledgerMovements.length} ردیف · واریز {formatRial(ledgerDeposits)} · برداشت{' '}
              {formatRial(ledgerWithdrawals)}
            </span>
          </CardTitle>
          <div className="space-y-2">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
              <div className="lg:col-span-2">
                <label className="mb-1 block text-xs text-slate-600">شخص / طرف حساب</label>
                <SearchableSelect
                  value={filterBeneficiaryId}
                  onChange={setFilterBeneficiaryId}
                  options={[{ value: '', label: 'همه افراد' }, ...beneficiaryFilterOptions]}
                  placeholder="انتخاب شخص…"
                  emptyMessage="شخصی یافت نشد"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-600">از تاریخ</label>
                <PersianDatePickerFa
                  value={filterDateFrom}
                  onChange={handleFilterDateFromChange}
                  placeholder="از تاریخ…"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-600">تا تاریخ</label>
                <PersianDatePickerFa
                  value={filterDateTo}
                  onChange={handleFilterDateToChange}
                  placeholder="تا تاریخ…"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-600">واریز / برداشت</label>
                <SearchableSelect
                  value={filterDirection}
                  onChange={(v) => setFilterDirection((v as '' | 'deposit' | 'withdrawal') || '')}
                  options={[
                    { value: '', label: 'همه' },
                    { value: 'deposit', label: 'فقط واریز' },
                    { value: 'withdrawal', label: 'فقط برداشت' },
                  ]}
                  placeholder="جهت…"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-xs text-slate-600">مبلغ از</label>
                  <Input
                    inputMode="numeric"
                    value={filterAmountMin}
                    onChange={(e) => setFilterAmountMin(e.target.value)}
                    placeholder="حداقل"
                    className="h-10"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-slate-600">مبلغ تا</label>
                  <Input
                    inputMode="numeric"
                    value={filterAmountMax}
                    onChange={(e) => setFilterAmountMax(e.target.value)}
                    placeholder="حداکثر"
                    className="h-10"
                  />
                </div>
              </div>
            </div>
            {filterDateError ? (
              <p className="text-xs text-rose-600">{filterDateError}</p>
            ) : null}
            {(filterTypeId || filterTypeLabel || filterDocNo || filterDescription) ? (
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                {filterTypeId || filterTypeLabel ? (
                  <span className="rounded-md bg-sky-50 px-2 py-1 text-sky-800">
                    نوع هزینه: {filterTypeLabel || filterTypeId}
                  </span>
                ) : null}
                {filterDocNo ? (
                  <span className="rounded-md bg-sky-50 px-2 py-1 text-sky-800">سند: {filterDocNo}</span>
                ) : null}
                {filterDescription ? (
                  <span className="rounded-md bg-sky-50 px-2 py-1 text-sky-800" title={filterDescription}>
                    شرح: {filterDescription.length > 40 ? `${filterDescription.slice(0, 40)}…` : filterDescription}
                  </span>
                ) : null}
              </div>
            ) : null}
            {hasActiveFilter ? (
              <div className="flex justify-end">
                <Button type="button" variant="outline" size="sm" onClick={clearLedgerFilters}>
                  پاک کردن همه فیلترها
                </Button>
              </div>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto pt-4">
          {advancesError ? (
            <p className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {advancesError}
            </p>
          ) : null}
          {ledgerMovements.length > 0 ? (
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="bg-slate-100 text-right">
                  <th className="border px-2 py-2">تاریخ</th>
                  <th className="border px-2 py-2">جهت</th>
                  <th className="border px-2 py-2">نوع هزینه</th>
                  <th className="border px-2 py-2">طرف حساب</th>
                  <th className="border px-2 py-2">سند</th>
                  <th className="border px-2 py-2">واریز</th>
                  <th className="border px-2 py-2">برداشت</th>
                  <th className="border px-2 py-2">شرح</th>
                  {onPrintVoucher ? <th className="border px-2 py-2">فیش</th> : null}
                </tr>
              </thead>
              <tbody>
                {ledgerMovements.map((row) => {
                  const kind = movementKind(row);
                  const credit = Number(row.credit ?? 0);
                  const debit = Number(row.debit ?? row.amount ?? 0);
                  const rowType = typeLabel(row);

                  return (
                    <tr key={row.id}>
                      <td className="border px-2 py-1.5 whitespace-nowrap font-mono text-xs">
                        {row.dateFa ? (
                          <FilterCellButton
                            title={`فیلتر روی تاریخ ${row.dateFa}`}
                            onClick={() => applyLedgerDateFilter(row.dateFa)}
                            className="font-mono text-xs"
                          >
                            {row.dateFa}
                          </FilterCellButton>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="border px-2 py-1.5">
                        <button
                          type="button"
                          title={kind === 'deposit' ? 'فیلتر فقط واریز' : 'فیلتر فقط برداشت'}
                          onClick={() => setFilterDirection(kind)}
                          className="rounded-full"
                        >
                          <Badge
                            variant="secondary"
                            className={
                              kind === 'deposit'
                                ? 'cursor-pointer bg-emerald-100 text-[10px] text-emerald-900 hover:ring-1 hover:ring-emerald-400'
                                : 'cursor-pointer bg-rose-100 text-[10px] text-rose-900 hover:ring-1 hover:ring-rose-400'
                            }
                          >
                            {kind === 'deposit' ? 'واریز' : 'برداشت'}
                          </Badge>
                        </button>
                      </td>
                      <td className="border px-2 py-1.5 max-w-[180px]">
                        <div className="flex min-w-0 items-center gap-1">
                          <div className="min-w-0 flex-1 truncate" title={rowType}>
                            {rowType && rowType !== '—' ? (
                              <FilterCellButton
                                title={`فیلتر روی نوع هزینه «${rowType}»`}
                                onClick={() => applyTypeFilter(row)}
                              >
                                {rowType}
                              </FilterCellButton>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </div>
                          {canEditExpenseType?.(row) && onEditExpenseType ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 shrink-0 p-0 text-slate-500 hover:text-sky-700"
                              title="ویرایش نوع هزینه"
                              onClick={() => onEditExpenseType(row)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          ) : null}
                        </div>
                      </td>
                      <td className="border px-2 py-1.5 max-w-[160px] truncate">
                        {row.beneficiaryId ? (
                          <FilterCellButton
                            title={`فیلتر روی ${row.beneficiary || 'این طرف حساب'}`}
                            onClick={() => setFilterBeneficiaryId(row.beneficiaryId)}
                          >
                            {row.beneficiary || '—'}
                          </FilterCellButton>
                        ) : (
                          row.beneficiary || '—'
                        )}
                      </td>
                      <td className="border px-2 py-1.5 font-mono text-xs">
                        {row.docNo ? (
                          <FilterCellButton
                            title={`فیلتر روی سند ${row.docNo}`}
                            onClick={() => setFilterDocNo(String(row.docNo))}
                            className="font-mono text-xs"
                          >
                            {row.docNo}
                          </FilterCellButton>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="border px-2 py-1.5 font-mono whitespace-nowrap text-emerald-700">
                        {credit > 0 ? (
                          <FilterCellButton
                            title={`فیلتر روی مبلغ واریز ${formatRial(credit)}`}
                            onClick={() => applyAmountFilter(credit, 'deposit')}
                            className="font-mono text-emerald-700"
                          >
                            {formatRial(credit)}
                          </FilterCellButton>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="border px-2 py-1.5 font-mono whitespace-nowrap text-rose-700">
                        {kind === 'withdrawal' && debit > 0 ? (
                          <FilterCellButton
                            title={`فیلتر روی مبلغ برداشت ${formatRial(debit)}`}
                            onClick={() => applyAmountFilter(debit, 'withdrawal')}
                            className="font-mono text-rose-700"
                          >
                            {formatRial(debit)}
                          </FilterCellButton>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="border px-2 py-1.5 max-w-[220px] truncate" title={row.description}>
                        {row.description ? (
                          <FilterCellButton
                            title={`فیلتر روی شرح «${row.description}»`}
                            onClick={() => setFilterDescription(row.description)}
                          >
                            {row.description}
                          </FilterCellButton>
                        ) : (
                          '—'
                        )}
                      </td>
                      {onPrintVoucher ? (
                        <td className="border px-2 py-1.5">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 whitespace-nowrap"
                            onClick={() => onPrintVoucher(row)}
                          >
                            <Printer className="ml-1 h-3.5 w-3.5" />
                            فیش
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-slate-500">
              {hasActiveFilter
                ? 'با این فیلترها ردیفی یافت نشد.'
                : 'هنوز گردش مالی برای این پروژه ثبت نشده است.'}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-3 md:grid-cols-2">
        <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">درصد مصرف پیمان</CardTitle>
          </CardHeader>
          <CardContent className="text-lg font-semibold">{expenseRatio.toFixed(1)}%</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">خالص گردش (واریز − برداشت)</CardTitle>
          </CardHeader>
          <CardContent
            className={`text-lg font-semibold ${
              totalDeposits - totalWithdrawals >= 0 ? 'text-emerald-700' : 'text-rose-700'
            }`}
          >
            {formatRial(totalDeposits - totalWithdrawals)}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">شاخص‌های پیش‌بینی</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border bg-slate-50 p-3">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Percent className="h-4 w-4" /> پیشرفت فیزیکی
            </div>
            <div className="mt-1 text-lg font-semibold">{progressPercent}%</div>
          </div>
          <div className="rounded-xl border bg-slate-50 p-3">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Wallet className="h-4 w-4" /> برآورد هزینه نهایی
            </div>
            <div className="mt-1 text-lg font-semibold">{formatRial(expectedFinalCost)}</div>
          </div>
          <div className="rounded-xl border bg-slate-50 p-3">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              {expectedProfitAtCompletion >= 0 ? (
                <TrendingUp className="h-4 w-4" />
              ) : (
                <TrendingDown className="h-4 w-4" />
              )}
              سود/زیان در پایان پروژه
            </div>
            <div
              className={`mt-1 text-lg font-semibold ${
                expectedProfitAtCompletion >= 0 ? 'text-emerald-700' : 'text-rose-700'
              }`}
            >
              {formatRial(expectedProfitAtCompletion)}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="border-b bg-gradient-to-r from-fuchsia-50 to-indigo-50">
          <CardTitle className="text-base">تحلیل سریع هزینه‌ها (برداشت‌ها)</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border bg-white p-3">
            <div className="mb-2 inline-flex items-center gap-1 text-sm font-semibold text-slate-700">
              <BarChart3 className="h-4 w-4" />
              سهم هزینه بر اساس نوع
            </div>
            <div className="space-y-2">
              {expenseByType.length ? (
                expenseByType.map((x) => (
                  <div key={x.label}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-slate-600">{x.label}</span>
                      <span className="font-mono text-slate-700">{formatRial(x.value)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100">
                      <div
                        className="h-2 rounded-full bg-indigo-500"
                        style={{ width: `${Math.max(8, (x.value / maxTypeValue) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-xs text-slate-500">داده‌ای برای نمایش وجود ندارد.</div>
              )}
            </div>
          </div>
          <div className="rounded-xl border bg-white p-3">
            <div className="mb-2 inline-flex items-center gap-1 text-sm font-semibold text-slate-700">
              <BarChart3 className="h-4 w-4" />
              سهم هزینه بر اساس شخص/طرف
            </div>
            <div className="space-y-2">
              {expenseByBeneficiary.length ? (
                expenseByBeneficiary.map((x) => (
                  <button
                    key={x.label}
                    type="button"
                    className="block w-full text-right hover:opacity-80"
                    title="فیلتر روی این شخص"
                    onClick={() => {
                      const match = beneficiaryFilterOptions.find((o) => o.label === x.label);
                      if (match) setFilterBeneficiaryId(match.value);
                    }}
                  >
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-slate-600">{x.label}</span>
                      <span className="font-mono text-slate-700">{formatRial(x.value)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100">
                      <div
                        className="h-2 rounded-full bg-cyan-500"
                        style={{ width: `${Math.max(8, (x.value / maxBeneficiaryValue) * 100)}%` }}
                      />
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-xs text-slate-500">داده‌ای برای نمایش وجود ندارد.</div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectWorkspaceOverviewTab;
